import joi from 'joi';

import {
  type AttributeFilters,
  attributeFiltersSchema,
} from '../attributeFilters';
import { protocolEdgeTypes } from '../edges/constants';
import { pointSchema } from '../elements';
import {
  type GraphOverlayRequest,
  graphOverlayRequestSchema,
} from '../graphOverlay/GraphOverlayRequest';
import {
  type GroupEdgeSettings,
  type HideableNodeType,
  type PathLookupEdgeSettings,
  type PathLookupSettings,
  type TopologyEdgeSettings,
  generateEdgeSettingsSchemas,
  groupEdgeSettingsSchema,
  hideableNodeTypeSchema,
  pathLookupSettingsSchema,
} from '../graphSettings';
import { type NodePositions, nodePositionsSchema } from '../nodes';
import type { WithSnapshot } from '../snapshot';
import type { PartialOnly } from '../types/PartialOnly';
import {
  type GraphPathLookupParameters,
  graphPathLookupParametersSchema,
} from './GraphPathLookupParameters';
import {
  type GraphTopologyParameters,
  graphTopologyParametersSchema,
} from './GraphTopologyParameters';

export type TopologyRequestEdgeSettings = PartialOnly<
  TopologyEdgeSettings,
  'id' | 'grouped' | 'labels' | 'labelsOptions' | 'style' | 'type' | 'visible'
>;

const topologyRequestEdgeSettingsSchemas = generateEdgeSettingsSchemas(
  'protocol',
  (s) =>
    s.fork(
      ['id', 'grouped', 'labels', 'labelsOptions', 'style', 'type', 'visible'],
      (schema) => schema.optional(),
    ),
);

export type PathLookupRequestEdgeSettings = PartialOnly<
  PathLookupEdgeSettings,
  'id' | 'grouped' | 'labels' | 'labelsOptions' | 'style' | 'type' | 'visible'
>;

export const pathLookupRequestEdgeSettingsSchemas = generateEdgeSettingsSchemas(
  'pathLookupEdge',
  (s) =>
    s.fork(
      ['id', 'grouped', 'labels', 'labelsOptions', 'style', 'type', 'visible'],
      (schema) => schema.optional(),
    ),
);

export type GroupRequestEdgeSettings = Omit<
  PartialOnly<
    GroupEdgeSettings,
    'id' | 'grouped' | 'label' | 'labelsOptions' | 'style'
  >,
  'children'
> & {
  children: (TopologyRequestEdgeSettings | PathLookupRequestEdgeSettings)[];
};

export const groupRequestEdgeSettingsSchema = groupEdgeSettingsSchema
  .fork(['id', 'grouped', 'label', 'labelsOptions', 'style'], (schema) =>
    schema.optional(),
  )
  .keys({
    children: joi
      .array()
      .items(
        ...topologyRequestEdgeSettingsSchemas,
        ...pathLookupRequestEdgeSettingsSchemas,
      )
      .min(1)
      .required(),
  });

export type RequestEdgeSettings =
  | GroupRequestEdgeSettings
  | TopologyRequestEdgeSettings
  | PathLookupRequestEdgeSettings;

export const requestEdgeSettingsSchemas = [
  groupRequestEdgeSettingsSchema,
  ...topologyRequestEdgeSettingsSchemas,
  ...pathLookupRequestEdgeSettingsSchemas,
];

export interface TopologyGraphRequestSettings {
  edges?: (TopologyRequestEdgeSettings | GroupRequestEdgeSettings)[];
  hiddenDeviceTypes?: HideableNodeType[];
}

export const topologyGraphRequestSettingsSchmea = joi.object().keys({
  edges: joi
    .array()
    .items(
      ...[
        groupRequestEdgeSettingsSchema,
        ...topologyRequestEdgeSettingsSchemas,
      ],
    ),
  hiddenDeviceTypes: joi.array().items(hideableNodeTypeSchema),
});

export interface PathLookupGraphRequestSettings {
  edges?: PathLookupRequestEdgeSettings[];
  pathLookup?: PathLookupSettings;
}

export const pathLookupGraphRequestSettingsSchema = joi.object().keys({
  edges: joi.array().items(...pathLookupRequestEdgeSettingsSchemas),
  pathLookup: pathLookupSettingsSchema,
});

export type GraphRequestSettings =
  | TopologyGraphRequestSettings
  | PathLookupGraphRequestSettings;

export const graphRequestSettingsSchema = joi.alternatives([
  topologyGraphRequestSettingsSchmea,
  pathLookupGraphRequestSettingsSchema,
]);

export interface GraphRequestBody extends WithSnapshot {
  attributeFilters?: AttributeFilters;
  settings?: GraphRequestSettings;
  positions?: NodePositions;
  overlay?: GraphOverlayRequest;
  parameters: GraphTopologyParameters | GraphPathLookupParameters;
}

export interface GraphDefaultGatewayRequestBody extends WithSnapshot {
  parameters: GraphPathLookupParameters;
}

export interface GraphDefaultGatewayResponseBody {
  source: string[];
  target: string[];
}

export const graphRequestBodySchema = joi.object().keys({
  attributeFilters: attributeFiltersSchema,
  snapshot: joi.string().required(),
  settings: graphRequestSettingsSchema,
  positions: nodePositionsSchema,
  overlay: graphOverlayRequestSchema,
  parameters: joi
    .alternatives([
      graphTopologyParametersSchema,
      graphPathLookupParametersSchema,
    ])
    .required(),
});

export const graphDefaultGatewayRequestBodySchema = joi.object().keys({
  snapshot: joi.string().required(),
  parameters: graphPathLookupParametersSchema.required(),
});

/**
 * Visio graph request body schema
 */

const visioBaseEdgeSchema = joi.object().keys({
  direction: joi
    .string()
    .valid('directed', 'undirected', 'bidirected')
    .required(),
  edgeSettingsId: joi.string().required(),
  id: joi.string().required(),
  labels: joi
    .object()
    .pattern(
      joi.string(),
      joi
        .array()
        .items(
          joi.object().keys({
            type: joi.string().required(),
            visible: joi.boolean().required(),
            text: joi.string().allow('').required(),
            wrapText: joi.boolean(),
          }),
        )
        .required(),
    )
    .required(),
  positions: joi
    .object()
    .keys({
      arrowHeads: joi.object().keys({
        source: joi.array().items(pointSchema),
        target: joi.array().items(pointSchema),
      }),
      labels: joi.object().keys({
        source: joi.object().keys({
          anchor: pointSchema.required(),
          angle: joi.number().required(),
          position: pointSchema.required(),
        }),
        center: joi.object().keys({
          anchor: pointSchema.required(),
          angle: joi.number().required(),
          position: pointSchema.required(),
        }),
        target: joi.object().keys({
          anchor: pointSchema.required(),
          angle: joi.number().required(),
          position: pointSchema.required(),
        }),
      }),
      line: joi.array().items(
        joi.alternatives(
          pointSchema,
          joi.object().keys({
            c: pointSchema,
            e: pointSchema,
          }),
        ),
      ),
      circle: joi
        .object()
        .keys({
          radius: joi.number(),
          position: pointSchema,
        })
        .optional(),
    })
    .allow(null),
  source: joi.string().required(),
  target: joi.string().required(),
  shift: joi.number().required(),
  style: joi
    .object()
    .keys({
      line: joi.object().keys({
        pattern: joi.string(),
        color: joi.number(),
        thickness: joi.number(),
      }),
      circle: joi
        .object()
        .keys({
          color: joi.number(),
        })
        .optional(),
      arrowHeads: joi
        .object()
        .keys({
          target: joi.object().keys({
            color: joi.number(),
          }),
        })
        .optional(),
    })
    .required(),
  intentCheckResult: joi.number().allow(null).optional(),
});
const visioPathLookupEdgeSchema = visioBaseEdgeSchema.keys({
  nextEdgeIds: joi.array().items(joi.string()),
  prevEdgeIds: joi.array().items(joi.string()),
  packet: joi.array().items(joi.object()),
  severityInfo: joi.object().keys({
    0: joi.number().required(),
    10: joi.number().required(),
    20: joi.number().required(),
    30: joi.number().required(),
  }),
  loopIds: joi.array().items(joi.number()),
  sourceIfaceName: joi.string(),
  targetIfaceName: joi.string(),
  trafficScore: joi.object().keys({
    accepted: joi.number().required(),
    dropped: joi.number().required(),
    forwarded: joi.number().required(),
    total: joi.number().required(),
  }),
});
const visioTopologyEdgeSchema = visioBaseEdgeSchema.keys({
  children: joi.array().items(joi.string()).min(1).required(),
  circle: joi.boolean().optional(),
  protocol: joi
    .string()
    .valid(...protocolEdgeTypes)
    .optional(),
  relatedTechnology: joi.object().optional(),
});
const visioEdgeSchema = joi.alternatives(
  visioPathLookupEdgeSchema,
  visioTopologyEdgeSchema,
);

export const visioGraphRequestBodySchema = joi.object().keys({
  graph: joi.object().keys({
    boxLabels: joi.object().pattern(joi.string(), joi.string().required()),
    edges: joi.object().pattern(joi.string(), visioEdgeSchema),
    nodes: joi.object().pattern(
      joi.string(),
      joi.object().keys({
        position: pointSchema.required(),
        boxId: joi.string().allow(null).required(),
        children: joi.array().items(joi.string()),
        graphType: joi.string().required(),
        id: joi.string().required(),
        label: joi.string().required(),
        parentPath: joi.string().allow(null).required(),
        sn: joi.string().required(),
        stack: joi.boolean(),
        path: joi.string(),
        type: joi.string().required(),
        style: joi.object(),
        generatedPackets: joi.object(),
        acceptedPackets: joi.object(),
        droppedPackets: joi.object(),
        extra: joi.object().allow(null),
        intentCheckResult: joi.number().allow(null).optional(),
      }),
    ),
  }),
});
